import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Mail, User as UserIcon } from "lucide-react";
import { gravatarUrl } from "@/lib/gravatar";

export default function ProfileDialog({ open, onOpenChange, user }) {
  const [tab, setTab] = useState("profile");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const avatarUrl = user?.email ? gravatarUrl(user.email) : null;

  const changePassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-zinc-200 dark:border-zinc-800 sm:max-w-md w-full sm:w-auto h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto p-0 sm:p-0">
        <DialogHeader className="p-4 sm:p-6 pb-2 border-b border-zinc-200 dark:border-zinc-800 sticky top-0 bg-white dark:bg-zinc-950 z-10">
          <DialogTitle className="font-display text-xl font-bold tracking-tight text-zinc-950 dark:text-zinc-100 sr-only">
            Profile
          </DialogTitle>
        </DialogHeader>

        {/* Tab bar */}
        <div className="flex border-b border-zinc-200 dark:border-zinc-800 px-4 sm:px-6">
          <button
            onClick={() => setTab("profile")}
            className={`pb-3 pt-2 text-xs font-bold uppercase tracking-[0.1em] border-b-2 transition-colors mr-6 ${
              tab === "profile"
                ? "border-zinc-900 dark:border-white text-zinc-900 dark:text-white"
                : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => setTab("password")}
            className={`pb-3 pt-2 text-xs font-bold uppercase tracking-[0.1em] border-b-2 transition-colors ${
              tab === "password"
                ? "border-zinc-900 dark:border-white text-zinc-900 dark:text-white"
                : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
            }`}
          >
            Password
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {tab === "profile" && (
            <div className="space-y-6">
              {/* Gravatar avatar */}
              <div className="flex items-center gap-4">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="h-16 w-16 border border-zinc-200 dark:border-zinc-700"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="h-16 w-16 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center bg-zinc-50 dark:bg-zinc-900">
                    <UserIcon className="h-6 w-6 text-zinc-400" />
                  </div>
                )}
                <div>
                  <div className="font-display text-lg font-bold tracking-tight text-zinc-950 dark:text-zinc-100">{user?.name}</div>
                  <div className="text-sm text-zinc-500 flex items-center gap-1.5 mt-0.5">
                    <Mail className="h-3.5 w-3.5" />
                    {user?.email}
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1.5">
                    Avatar from <a href="https://gravatar.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-zinc-900 dark:hover:text-white transition-colors">Gravatar</a> — update yours there.
                  </p>
                </div>
              </div>
            </div>
          )}

          {tab === "password" && (
            <form onSubmit={changePassword} className="space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2 text-zinc-900 dark:text-zinc-100">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
                  placeholder="Enter current password"
                  data-testid="change-password-current"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2 text-zinc-900 dark:text-zinc-100">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
                  placeholder="Min 6 characters"
                  data-testid="change-password-new"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2 text-zinc-900 dark:text-zinc-100">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
                  placeholder="Repeat new password"
                  data-testid="change-password-confirm"
                />
              </div>
              {error && (
                <div className="border border-red-500 text-red-600 px-4 py-3 text-sm">{error}</div>
              )}
              <button
                type="submit"
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-black dark:bg-white text-white dark:text-black py-3.5 text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50"
                data-testid="change-password-submit"
              >
                <Lock className="h-4 w-4" />
                {saving ? "Updating..." : "Update password"}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
